// Shopify asset_url supplies the content version; arbitrary query strings can serve a stale CDN module.
const rulesUrl = document.querySelector('fs-welcome-offer[data-rules-url]')?.dataset.rulesUrl;
const { DAY, readPolicy, storageKeys, dismissals, endpointPath, localDestination, newVisit, mayAutoShow, validatedReceipt, validatedSimulation, countdown } = await import(rulesUrl);

const LEGACY_KEY = 'fs:welcome:v1';
const TERMINAL_PREVIEWS = {
  checking: ['Checking your offer…', 'Retrieving the original code and its current status.', false],
  expired: ['This offer has expired.', 'This code is no longer valid. Its 24-hour window cannot be restarted. Look out for our next campaign.', false],
  used: ['You’ve already used this offer.', 'This campaign’s offer can be used once per customer. Look out for our next campaign.', false],
  error: ['We couldn’t retrieve your offer.', 'Please try again. Retrieving your offer will not create a new code or extend its deadline.', true]
};
const memory = new Map();
function read(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null') || memory.get(key) || {}; }
  catch { return memory.get(key) || {}; }
}
function write(key, value) {
  memory.set(key, value);
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* Browsing and manual close must still work. */ }
}

class WelcomeOffer extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.abort = new AbortController();
    this.events = { signal: this.abort.signal };
    this.preview = this.dataset.preview === 'true';
    this.backendTest = this.dataset.backendTest === 'true';
    this.designOnly = this.preview && !this.backendTest;
    this.subscribed = this.dataset.subscribed === 'true';
    this.policy = readPolicy(this.dataset);
    this.endpoint = endpointPath(this.dataset.endpoint);
    this.media = matchMedia('(max-width: 767px)');
    this.dialog = this.querySelector('[data-wo-dialog]');
    this.bar = this.querySelector('[data-wo-mobile-bar]');
    this.form = this.querySelector('[data-wo-form]');
    this.submit = this.querySelector('[data-wo-submit]');
    this.submitMarkup = this.submit.innerHTML;
    this.available = false;
    this.receipt = null;
    this.simulation = false;
    if (!this.policy || (!this.designOnly && !this.endpoint)) return;
    this.keys = storageKeys(this.policy.campaign, this.preview, this.dataset.customerScope);
    // Design previews restart on each page load without changing saved customer records.
    this.state = this.designOnly ? {} : read(this.keys.state);
    // Keep prior dismissal choices, but never migrate the old subscriber/order suppression.
    this.frequency = this.preview ? {} : dismissals(read(this.keys.frequency), read(LEGACY_KEY));
    this.visit = newVisit(this.preview ? null : read(this.keys.visit), Date.now());
    if (!this.preview) {
      write(this.keys.frequency, this.frequency);
      write(this.keys.visit, this.visit);
    }
    this.querySelectorAll('[data-wo-close]').forEach(button => button.addEventListener('click', () => this.close(true), this.events));
    this.querySelector('[data-wo-dismiss-bar]').addEventListener('click', () => this.dismissBar(), this.events);
    this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(true); }, this.events);
    this.dialog.addEventListener('click', event => {
      const box = this.dialog.getBoundingClientRect();
      if (event.target === this.dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) this.close(true);
    }, this.events);
    this.dialog.addEventListener('close', () => {
      this.dialog.removeAttribute('style');
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    }, this.events);
    this.form.addEventListener('submit', event => this.claim(event), this.events);
    this.addEventListener('click', event => { const button = event.target.closest('[data-wo-open]'); if (button) this.open(button); }, this.events);
    this.querySelector('[data-wo-copy]').addEventListener('click', () => this.copy(), this.events);
    this.querySelector('[data-wo-retry]').addEventListener('click', () => this.restore(), this.events);
    this.querySelector('[data-wo-shop]').addEventListener('click', event => {
      if (!this.receipt || countdown(this.receipt.expiresAt, this.serverNow()).expired) { event.preventDefault(); this.tick(); }
    }, this.events);
    document.addEventListener('fs:newsletter-received', () => { this.subscribed = true; this.renderConsent(); }, this.events);
    if (document.querySelector('[data-fs-feedback="newsletter"] [data-fs-form-success]')) this.subscribed = true;
    window.addEventListener('storage', event => {
      if (this.preview) return;
      if (event.key === this.keys.state) {
        this.state = read(this.keys.state);
        if (this.state.status) { this.bar.hidden = true; this.installAnnouncement(); if (this.dialog.open && !this.submitting) this.restore(); }
      }
      if (event.key === this.keys.frequency) { this.frequency = read(this.keys.frequency); this.bar.hidden = true; }
      if (event.key === this.keys.visit) this.visit = newVisit(read(this.keys.visit), Date.now());
    }, this.events);
    document.addEventListener('visibilitychange', () => { this.lastTick = performance.now(); if (document.visibilityState === 'visible') this.tick(); }, this.events);
    this.media.addEventListener('change', () => {
      this.elapsed = 0;
      this.mobileElapsed = 0;
      this.bar.hidden = true;
      if (this.dialog.open) this.close(false);
    }, this.events);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', () => {
      if (!this.media.matches || !this.dialog.open) return;
      const viewport = window.visualViewport;
      const keyboard = window.innerHeight - viewport.height - viewport.offsetTop;
      this.dialog.style.bottom = `${Math.max(0, keyboard)}px`;
      this.dialog.style.maxHeight = `${Math.max(180, viewport.height - 32)}px`;
    }, this.events);
    this.renderPolicy();
    this.renderConsent();
    this.prepare();
  }

  disconnectedCallback() {
    this.abort?.abort();
    clearInterval(this.interval);
    this.announcement?.remove();
    this.footerEntry?.remove();
    if (this.dialog?.open) this.dialog.close();
    this.started = false;
  }

  async request(body) {
    const controller = new AbortController();
    const cancel = () => controller.abort();
    this.abort.signal.addEventListener('abort', cancel, { once: true });
    const timeout = setTimeout(cancel, 12000);
    try {
      const response = await fetch(this.endpoint, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {}), signal: controller.signal
      });
      if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) throw new Error('unavailable');
      return await response.json();
    } finally {
      clearTimeout(timeout);
      this.abort.signal.removeEventListener('abort', cancel);
    }
  }

  async prepare() {
    const checksBackend = !this.designOnly;
    if (this.preview) {
      if (checksBackend) this.designOnly = true;
      this.startPresentation();
      if (this.terminalPreview()) await this.open();
    }
    if (checksBackend) {
      try {
        const response = await this.request();
        const serverPolicy = readPolicy(response.policy || {});
        if (!response.ready || !serverPolicy || serverPolicy.key !== this.policy.key) throw new Error('unavailable');
        if ((this.backendTest && !['mock', 'test'].includes(response.mode)) || (!this.preview && ['mock', 'test'].includes(response.mode))) throw new Error('unavailable');
        this.simulation = response.mode === 'mock';
        this.offerKey = response.offerKey;
        if (typeof this.offerKey !== 'string' || this.offerKey.length < 16) throw new Error('unavailable');
        if (response.customerOffer) {
          const existing = validatedReceipt(response.customerOffer, this.policy);
          if (!existing) throw new Error('unavailable');
          this.remember(existing);
        }
        this.designOnly = false;
        this.backendUnavailable = false;
      } catch {
        if (!this.preview) return;
        this.designOnly = true;
        this.backendUnavailable = true;
        this.renderPreviewConnection();
        return;
      }
    }
    if (!this.preview) this.startPresentation();
    this.renderPreviewConnection();
  }

  startPresentation() {
    if (!this.isConnected || this.available) return;
    this.available = true;
    // Start the desktop photo before the timed prompt, rather than a blank first frame.
    if (!this.media.matches) this.querySelector('.wo-photo__image').loading = 'eager';
    this.installAnnouncement();
    this.elapsed = 0;
    this.mobileElapsed = 0;
    this.lastTick = performance.now();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  renderPreviewConnection() {
    if (!this.isConnected) return;
    if (this.backendUnavailable) {
      const note = this.querySelector('.wo-preview-context');
      if (note) note.textContent = 'UAT backend unavailable · preview only';
    } else if (this.backendTest) {
      const note = this.querySelector('.wo-preview-context');
      if (note) note.textContent = this.simulation
        ? 'UAT mock · simulated codes are not valid at checkout'
        : 'Draft backend test · real offers for approved test emails';
    }
  }

  terminalPreview() {
    if (!this.preview) return null;
    const state = new URLSearchParams(window.location.search).get('welcome_offer_preview_state');
    return TERMINAL_PREVIEWS[state] || null;
  }

  installAnnouncement() {
    if (!this.available) return;
    const label = this.state.status ? 'View your offer' : `${this.policy.percentage}% off your next order`;
    if (this.announcement) {
      this.announcement.querySelector('[data-wo-bar-label]').textContent = label;
      if (this.footerEntry) this.footerEntry.textContent = `${label} →`;
      return;
    }
    const announcement = document.querySelector('.announcement-bar');
    if (!announcement) return;
    this.announcement = this.querySelector('[data-wo-announcement-template]').content.firstElementChild.cloneNode(true);
    this.announcement.querySelector('[data-wo-bar-label]').textContent = label;
    this.announcement.addEventListener('click', () => this.open(this.announcement), this.events);
    announcement.append(this.announcement);
    this.footerEntry = document.createElement('button');
    this.footerEntry.type = 'button';
    this.footerEntry.className = 'wo-footer-entry';
    this.footerEntry.textContent = `${label} →`;
    this.footerEntry.addEventListener('click', () => this.open(this.footerEntry), this.events);
    document.querySelector('.site-footer__lead')?.append(this.footerEntry);
  }

  renderPolicy() {
    this.querySelector('[data-wo-percent]').textContent = this.policy.percentage;
    this.querySelector('[data-wo-hours]').textContent = this.policy.hours;
    this.querySelector('[data-wo-minimum]').textContent = this.policy.minimum > 0 ? `Minimum ${new Intl.NumberFormat('en-US', { style: 'currency', currency: this.policy.currency }).format(this.policy.minimum)} in eligible items. ` : '';
  }

  renderConsent() {
    this.querySelector('[data-wo-consent]').textContent = this.subscribed
      ? this.dataset.subscribedCopy
      : this.dataset.consentCopy;
  }

  busyPage() {
    if (document.querySelector('dialog[open]:not(.wo-dialog), [aria-modal="true"]:not(.wo-dialog)')) {
      const blockers = [...document.querySelectorAll('dialog[open]:not(.wo-dialog), [aria-modal="true"]:not(.wo-dialog)')];
      if (blockers.some(node => node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden')) return true;
    }
    return Boolean([...document.querySelectorAll('[data-cookie-banner], #shopify-pc__banner')].some(node => node.getClientRects().length));
  }

  tick() {
    const now = performance.now();
    const elapsed = Math.min(1500, now - this.lastTick);
    this.lastTick = now;
    if (this.receipt) {
      const remaining = countdown(this.receipt.expiresAt, this.serverNow());
      this.querySelector('[data-wo-countdown]').textContent = remaining.text;
      if (remaining.expired) this.showReceipt({ ...this.receipt, status: 'expired' });
    }
    if (!this.available || document.visibilityState !== 'visible' || this.dialog.open || this.busyPage()) {
      if (this.busyPage()) this.bar.hidden = true;
      return;
    }
    if (!this.preview) {
      this.visit = newVisit(read(this.keys.visit), Date.now());
      if (Date.now() - Number(read(this.keys.visit).lastSeen || 0) >= 30000) write(this.keys.visit, this.visit);
      this.state = read(this.keys.state);
      this.frequency = read(this.keys.frequency);
    }
    const mobile = this.media.matches;
    if (!mayAutoShow({
      state: this.preview ? {} : this.state,
      frequency: this.frequency,
      visit: this.visit,
      now: Date.now(),
      mobile,
      home: this.dataset.home === 'true',
      visitThreshold: this.preview ? 1 : this.dataset.visitThreshold,
      repeatAfterThreshold: !this.preview && this.dataset.repeatAfterVisits === 'true'
    })) return;
    if (mobile) this.mobileElapsed += elapsed; else this.elapsed += elapsed;
    const threshold = Number(mobile ? this.dataset.mobileDelay : this.dataset.desktopDelay) * 1000;
    if ((mobile ? this.mobileElapsed : this.elapsed) < threshold) return;
    this.visit[mobile ? 'mobileShown' : 'desktopShown'] = true;
    if (!this.preview) this.visit.autoShownCount = Math.max(0, Number(this.visit.autoShownCount) || 0) + 1;
    if (!this.preview) write(this.keys.visit, this.visit);
    this.open();
  }

  async open(opener) {
    if (!this.available || this.busyPage() || this.dialog.open) return;
    this.opener = opener || document.activeElement;
    this.bar.hidden = true;
    this.dialog.showModal();
    // A manual preview counts for this page too; closing it must not immediately reopen it.
    if (this.preview) this.visit[this.media.matches ? 'mobileShown' : 'desktopShown'] = true;
    else this.state = read(this.keys.state);
    const terminalPreview = this.terminalPreview();
    if (terminalPreview) {
      this.showTerminal(...terminalPreview);
      return;
    }
    if (!this.designOnly && this.state.status) await this.restore();
  }

  async restore() {
    if (this.designOnly || this.submitting) return;
    if (this.simulation) {
      if (this.receipt) this.showReceipt(this.receipt);
      return;
    }
    this.showTerminal('Checking your offer…', 'Retrieving the original code and its current status.');
    if (!this.state.receiptToken) {
      this.showTerminal('This offer was already claimed.', 'This campaign can only be claimed once per email address.');
      return;
    }
    this.setBusy(true);
    try {
      const response = await this.request({ action: 'resume', campaign: this.policy.campaign, receiptToken: this.state.receiptToken });
      const receipt = validatedReceipt(response, this.policy);
      if (!receipt) throw new Error('unavailable');
      this.showReceipt(receipt);
    } catch {
      this.showTerminal('We couldn’t retrieve your offer.', 'Please try again. Retrieving your offer will not create a new code or extend its deadline.', true);
    } finally { this.setBusy(false); }
  }

  close(dismiss) {
    if (dismiss && !this.preview && !this.state.status) {
      const mobile = this.media.matches;
      const key = mobile ? 'mobileDismissedUntil' : 'desktopDismissedUntil';
      this.frequency = { ...read(this.keys.frequency), [key]: Date.now() + Number(mobile ? this.dataset.mobileDays : this.dataset.desktopDays) * DAY };
      write(this.keys.frequency, this.frequency);
    }
    this.bar.hidden = true;
    this.dialog.close();
  }

  dismissBar() {
    if (!this.preview) {
      this.frequency = { ...read(this.keys.frequency), mobileDismissedUntil: Date.now() + Number(this.dataset.mobileDays) * DAY };
      write(this.keys.frequency, this.frequency);
    }
    this.bar.hidden = true;
  }

  remember(receipt) {
    this.state = receipt.simulation
      ? { status: receipt.status }
      : { status: receipt.status, receiptToken: receipt.receiptToken };
    if (!receipt.simulation) write(this.keys.state, this.state);
    this.bar.hidden = true;
    this.installAnnouncement();
  }

  setBusy(value) {
    this.submitting = value;
    this.submit.disabled = value;
    this.form.setAttribute('aria-busy', String(value));
    if (value) this.submit.textContent = 'Preparing your discount…';
    else this.submit.innerHTML = this.submitMarkup;
    this.querySelector('[data-wo-retry]').disabled = value;
  }

  error(message) {
    const error = this.querySelector('[data-wo-error]');
    error.textContent = message;
    error.hidden = false;
  }

  async claim(event) {
    event.preventDefault();
    if (this.submitting || !this.form.reportValidity()) return;
    if (this.designOnly) { this.error('The offer service is not connected yet. Please try again later.'); return; }
    if (this.state.status) { await this.restore(); return; }
    const fields = new FormData(this.form);
    if (fields.get('company')) return;
    this.querySelector('[data-wo-error]').hidden = true;
    this.setBusy(true);
    try {
      const response = await this.request({ action: 'claim', campaign: this.policy.campaign, email: String(fields.get('email')).trim(), consent: !this.subscribed, offerKey: this.offerKey });
      if (response.status === 'subscription_required') {
        this.subscribed = false;
        this.renderConsent();
        this.error('Please review the email subscription notice, then submit again to agree and claim your offer.');
        return;
      }
      if (response.status === 'already_claimed' || response.status === 'check_email') {
        this.showTerminal('This offer was already claimed.', 'For your privacy, an existing code is not shown again without the original browser receipt. This campaign can only be claimed once per email address.');
        this.form.reset();
        return;
      }
      const receipt = this.simulation
        ? validatedSimulation(response, this.policy)
        : validatedReceipt(response, this.policy);
      if (!receipt) throw new Error('not-issued');
      // Store only an opaque server-issued receipt token. Never persist the email or invent a coupon.
      this.showReceipt(receipt);
      this.form.reset();
    } catch {
      this.error('We could not confirm your welcome offer. Please try again; your discount is only ready once a code appears here.');
    } finally { this.setBusy(false); }
  }

  showReceipt(receipt) {
    this.remember(receipt);
    if (receipt.status !== 'issued') {
      this.receipt = null;
      this.policy = receipt.policy;
      this.renderPolicy();
      this.showTerminal(receipt.status === 'used' ? 'You’ve already used this offer.' : 'This offer has expired.',
        receipt.status === 'used' ? 'This campaign’s offer can be used once per customer. Look out for our next campaign.' : 'This code is no longer valid. Its 24-hour window cannot be restarted. Look out for our next campaign.'.replace('24', String(receipt.policy.hours)));
      return;
    }
    this.receipt = receipt;
    this.receiptClock = performance.now();
    this.policy = receipt.policy;
    this.renderPolicy();
    this.querySelector('[data-wo-form-state]').hidden = true;
    this.querySelector('[data-wo-success-state]').hidden = false;
    this.querySelector('[data-wo-terminal-state]').hidden = true;
    const heading = this.querySelector('[data-wo-success-heading]');
    heading.id = 'wo-success-heading';
    this.dialog.setAttribute('aria-labelledby', heading.id);
    this.dialog.removeAttribute('aria-describedby');
    heading.textContent = `Enjoy ${receipt.policy.percentage}% off.`;
    this.querySelector('[data-wo-code]').textContent = receipt.code;
    const copyButton = this.querySelector('[data-wo-copy]');
    copyButton.disabled = false;
    copyButton.setAttribute('data-copied', 'false');
    copyButton.setAttribute('aria-label', this.dataset.copyLabel);
    copyButton.setAttribute('title', this.dataset.copyLabel);
    this.querySelector('[data-wo-copy-label]').textContent = this.dataset.copyLabel;
    this.querySelector('[data-wo-copy-feedback]').textContent = '';
    this.querySelector('[data-wo-copy-feedback]').removeAttribute('data-error');
    this.querySelector('[data-wo-consent-note]').hidden = true;
    this.querySelector('[data-wo-status]').hidden = false;
    this.querySelector('[data-wo-status]').textContent = receipt.simulation
      ? 'UAT simulated code · not valid at checkout'
      : receipt.subscription === 'pending' ? this.dataset.pendingCopy : this.dataset.issuedCopy;
    const root = localDestination(this.dataset.root || '/');
    const destination = localDestination(this.dataset.shoppingUrl);
    this.querySelector('[data-wo-shop]').href = receipt.simulation
      ? destination
      : `${root.replace(/\/$/, '')}/discount/${encodeURIComponent(receipt.code)}?redirect=${encodeURIComponent(destination)}`;
    const image = this.querySelector('[data-wo-success-image]');
    if (image && !this.successPhotoSet) { this.querySelector('[data-wo-photo]').replaceChildren(image.content.cloneNode(true)); this.successPhotoSet = true; }
    this.tick();
  }

  showTerminal(title, message, retry = false) {
    this.receipt = null;
    this.querySelector('[data-wo-consent-note]').hidden = true;
    this.querySelector('[data-wo-status]').hidden = true;
    this.querySelector('[data-wo-copy-feedback]').textContent = '';
    this.querySelector('[data-wo-form-state]').hidden = true;
    this.querySelector('[data-wo-success-state]').hidden = true;
    this.querySelector('[data-wo-terminal-state]').hidden = false;
    this.querySelector('[data-wo-terminal-heading]').textContent = title;
    this.querySelector('[data-wo-terminal-description]').textContent = message;
    this.querySelector('[data-wo-retry]').hidden = !retry;
    this.querySelector('[data-wo-copy]').disabled = true;
    this.querySelector('[data-wo-shop]').removeAttribute('href');
    this.dialog.setAttribute('aria-labelledby', 'wo-terminal-heading');
    this.dialog.removeAttribute('aria-describedby');
  }

  serverNow() { return this.receipt.serverNow + (performance.now() - this.receiptClock); }

  async copy() {
    if (!this.receipt || countdown(this.receipt.expiresAt, this.serverNow()).expired) return;
    try {
      await navigator.clipboard.writeText(this.receipt.code);
      const copyButton = this.querySelector('[data-wo-copy]');
      copyButton.setAttribute('data-copied', 'true');
      copyButton.setAttribute('aria-label', this.dataset.copiedLabel);
      copyButton.setAttribute('title', this.dataset.copiedLabel);
      this.querySelector('[data-wo-copy-label]').textContent = this.dataset.copiedLabel;
      this.querySelector('[data-wo-copy-feedback]').removeAttribute('data-error');
      this.querySelector('[data-wo-copy-feedback]').textContent = this.dataset.copiedLabel;
    } catch {
      this.querySelector('[data-wo-copy-feedback]').setAttribute('data-error', 'true');
      this.querySelector('[data-wo-copy-feedback]').textContent = this.dataset.copyFailed;
    }
  }
}

if (!customElements.get('fs-welcome-offer')) customElements.define('fs-welcome-offer', WelcomeOffer);
